import _ from 'lodash';
import {
  createContext,
  DependencyList,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

interface Breadcrumb {
  display: string;
  url: string;
}

interface BreadcrumbState {
  breadcrumbs: Breadcrumb[];
  clearBreadcrumbs: () => void;
  setBreadcrumbs: (breadcrumbs: Breadcrumb[]) => void;
}

const initialState = {
  breadcrumbs: [] as Breadcrumb[],
  clearBreadcrumbs: () => {},
  setBreadcrumbs: () => {},
};

export const BreadcrumbContext = createContext<BreadcrumbState>(initialState);

export const BreadcrumbContextProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const [breadcrumbs, setBreadcrumbs] = useState(initialState.breadcrumbs);
  const clearBreadcrumbs = useCallback(() => {
    setBreadcrumbs(initialState.breadcrumbs);
  }, []);

  const value = useMemo(
    () => ({
      breadcrumbs,
      clearBreadcrumbs,
      setBreadcrumbs,
    }),
    [breadcrumbs, clearBreadcrumbs],
  );

  return (
    <BreadcrumbContext.Provider value={value}>
      {children}
    </BreadcrumbContext.Provider>
  );
};

export const useBreadcrumbContext = () => useContext(BreadcrumbContext);

export const useSetBreadcrumbEffect = (
  breadcrumbs: Nullable<Breadcrumb[]>,
  dependencies: DependencyList,
) => {
  const { clearBreadcrumbs, setBreadcrumbs } = useBreadcrumbContext();

  useEffect(() => {
    if (breadcrumbs) {
      setBreadcrumbs(breadcrumbs);
      return clearBreadcrumbs;
    }
    return _.noop;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clearBreadcrumbs, setBreadcrumbs, ...dependencies]);
};
